'use client';

import { Check } from 'lucide-react';

interface StepIndicatorProps {
  currentStep: number;
  stepLabel: string;
  hidden: boolean;
  onStepClick: (step: number) => void;
  navigationDisabled: boolean;
}

/**
 * Desktop step circles with check marks. Lifted from dgr/register with
 * semantic tokens so the tenant CSS scope provides the brand colour.
 */
export function StepIndicator({
  currentStep,
  stepLabel,
  hidden,
  onStepClick,
  navigationDisabled,
}: StepIndicatorProps) {
  return (
    <div className={`border-b border-border/60 ${hidden ? 'hidden' : ''}`}>
      <div className="hidden items-center gap-2 px-6 py-4 md:flex">
        {[1, 2, 3, 4].map((step) => (
          <div key={step} className="flex items-center">
            <button
              onClick={() => step < currentStep && onStepClick(step)}
              disabled={step > currentStep || navigationDisabled}
              className={`
                flex h-9 w-9 items-center justify-center rounded-full text-sm font-semibold transition-all
                ${
                  step === currentStep
                    ? 'bg-primary text-primary-foreground'
                    : step < currentStep
                      ? `bg-primary/15 text-primary ${
                          navigationDisabled
                            ? 'cursor-not-allowed opacity-60'
                            : 'cursor-pointer hover:bg-primary/25'
                        }`
                      : 'bg-muted text-muted-foreground'
                }
              `}
            >
              {step < currentStep ? <Check className="h-4 w-4" /> : step}
            </button>
            {step < 4 && (
              <div
                className={`mx-0.5 h-0.5 w-8 ${step < currentStep ? 'bg-primary/40' : 'bg-muted'}`}
              />
            )}
          </div>
        ))}
        <span className="ml-3 text-sm text-muted-foreground">{stepLabel}</span>
      </div>
    </div>
  );
}
